import { User, UserGroups } from '@ravago/shared/authentication';

import { AccessLevel } from '../../enums/access-level';

export class AccessLevelUtils {
  private static readonly userGroupsByAccessLevel: Record<AccessLevel, UserGroups[]> = {
    [AccessLevel.PUBLIC]: [UserGroups.public],
    [AccessLevel.GUEST]: [UserGroups.digitalPlatformGuest],
    [AccessLevel.PENDING]: [UserGroups.digitalPlatformPendingVerification],
    [AccessLevel.CUSTOMER]: [UserGroups.customerPortal],
    [AccessLevel.INTERNAL]: [
      UserGroups.ravagoGlobalGroupAdmin,
      UserGroups.ravagoSalesRepresentative,
      UserGroups.ravagoGlobalViewer,
      UserGroups.ravagoGlobalEditor
    ],
    // These will be deprecated in a different ticket.
    // They need to stay for now to support the Contentful page access level field.
    [AccessLevel.AUTHENTICATED]: [
      UserGroups.customerPortal,
      UserGroups.ravagoGlobalGroupAdmin,
      UserGroups.ravagoSalesRepresentative,
      UserGroups.ravagoGlobalViewer,
      UserGroups.digitalPlatformGuest,
      UserGroups.digitalPlatformPendingVerification,
      UserGroups.ravagoGlobalEditor
    ],
    [AccessLevel.VERIFIED]: [
      UserGroups.customerPortal,
      UserGroups.ravagoGlobalGroupAdmin,
      UserGroups.ravagoSalesRepresentative,
      UserGroups.ravagoGlobalViewer
    ],
    [AccessLevel.CSR]: [UserGroups.ravagoGlobalGroupAdmin, UserGroups.ravagoSalesRepresentative, UserGroups.ravagoGlobalViewer]
  };

  static getUserGroupsByAccessLevel(accessLevels?: AccessLevel[]): UserGroups[] {
    const userGroups: UserGroups[] = [];

    accessLevels?.forEach((accessLevel) => {
      if (this.userGroupsByAccessLevel[accessLevel]) {
        userGroups.push(...this.userGroupsByAccessLevel[accessLevel]);
      }
    });

    return userGroups;
  }

  static canAccess(user?: User, allowedGroups?: UserGroups[], redirectUser?: boolean, internalName?: string): boolean {
    if (allowedGroups?.length === 1 && allowedGroups.includes(UserGroups.public) && !redirectUser) return !user;

    const specialCases = internalName ? this.getSpecialCases() : {};
    const specialCase = internalName ? specialCases[internalName] : null;

    /**
     * Special case handling for specific internal names.
     * The internal name can be set on a navigation item in Contentful.
     */
    if (specialCase) {
      const hasExcludedGroup = user?.groups.some((group) => specialCase.groups.includes(group));

      if (hasExcludedGroup) {
        return false;
      }
    }

    /**
     * Regular flow for checking access.
     */
    const isAllowed = user?.groups.some((group) => allowedGroups?.includes(group));

    return !!(isAllowed || allowedGroups?.includes(UserGroups.public));
  }

  private static getSpecialCases(): { [key: string]: { groups: UserGroups[] } } {
    return {
      'ravago-academy': {
        groups: [
          UserGroups.digitalPlatformPendingVerification,
          UserGroups.ravagoSalesRepresentative,
          UserGroups.ravagoGlobalViewer,
          UserGroups.ravagoGlobalEditor,
          UserGroups.ravagoGlobalGroupAdmin
        ]
      }
    };
  }
}
